/**
 * Phase1TaskGroup.tsx
 * A task split across ≥2 machines, rendered as a grouped header + one sub-row per
 * machine (Gigi, 2026-06-19). Replaces the old single-row-with-stacked-machines
 * layout so each machine's schedule can be read and dragged independently.
 *
 * Header row  → test-level summary: name, Test Qty, Machine Qty (editable),
 *   aggregate Start…End span, total working-day duration, total machine time, and
 *   the task-level delete. Clicking it flashes EVERY machine bar below.
 * Sub-rows    → Phase1MachineSubRow, one per booking (even qty split + own dates).
 *
 * Single-machine and unplaced tasks keep using Phase1TaskRow; Phase1Panel routes
 * to this component only when a task has >1 active booking.
 */

import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CheckCircleOutline, Delete } from '@mui/icons-material';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import { Phase1MachineSubRow } from './Phase1MachineSubRow';
import { MachineQtyEditor } from '../../../../components/schedule/MachineQtyEditor';
import {
  ConfirmDialog,
  CONFIRM_INITIAL,
  type ConfirmDialogState,
} from '../../../../components/shared/ConfirmDialog';
import type { BookingView, TaskWithBookings } from '../../../../services/scheduleTasks';
import { computeTaskDurationDays, taskDurationDisplay } from './taskDuration';
import { formatMachineTime, evenSplit } from './taskRowMath';

type StartChangeResult =
  | { kind: 'updated' }
  | { kind: 'needsConfirm'; conflict: boolean; weekend: boolean; earliestFree: string; conflictLabel?: string | null };

type QtyChangeResult =
  | { kind: 'updated' }
  | { kind: 'needsConfirm'; conflictLabel?: string | null };

export interface Phase1TaskGroupProps {
  task: TaskWithBookings;
  rowNumber: number | string;
  bulkMode: boolean;
  selected: boolean;
  holidays: readonly string[];
  onToggleSelect: (key: string) => void;
  onUpdateMachineCount: (taskKey: string, count: number) => Promise<void>;
  onChangeMachine: (bookingKey: string, machineKey: string) => Promise<void>;
  onChangeBookingStart: (
    bookingKey: string,
    startDate: string,
    opts?: { force?: boolean },
  ) => Promise<StartChangeResult>;
  /** Set one machine's per-test quantity (080) — recomputes that booking's EndDate. */
  onChangeBookingQty: (
    bookingKey: string,
    qty: number,
    opts?: { force?: boolean },
  ) => Promise<QtyChangeResult>;
  onDelete: (taskKey: string) => Promise<void>;
  /** Flash all of this task's machine bars in the grid. */
  onActivateAll: () => void;
  /** Flash a single machine's bar. */
  onActivateBooking: (bookingKey: string) => void;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

export const Phase1TaskGroup: React.FC<Phase1TaskGroupProps> = ({
  task,
  rowNumber,
  bulkMode,
  selected,
  holidays,
  onToggleSelect,
  onUpdateMachineCount,
  onChangeMachine,
  onChangeBookingStart,
  onChangeBookingQty,
  onDelete,
  onActivateAll,
  onActivateBooking,
}) => {
  const theme = useTheme();
  const [deleting, setDeleting] = useState(false);
  const [confirmState, setConfirmState] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // Apply a new machine count for THIS task. The editable count is the live
  // per-scenario booking count (task.Bookings.length), not the shared persisted
  // MachineCount. Reducing below the current bookings soft-deletes the extras AND
  // their hand-tuned dates, so confirm first (UX audit 2026-06-20); the commit runs
  // via onUpdateMachineCount (scenario-scoped reconcile).
  const applyCount = async (parsed: number): Promise<void> => {
    const removed = task.Bookings.length - parsed;
    if (removed > 0) {
      setConfirmState({
        open: true,
        title: 'Reduce machines?',
        message:
          `Reduce to ${parsed} machine${parsed === 1 ? '' : 's'}? This removes ` +
          `${removed} machine assignment${removed === 1 ? '' : 's'} and ` +
          `${removed === 1 ? 'its' : 'their'} scheduled dates. This can't be undone.`,
        confirmLabel: 'Reduce',
        confirmColor: 'warning',
        onConfirm: () => { void onUpdateMachineCount(task.ScheduleTaskKey, parsed); },
      });
      return; // commit happens on confirm
    }
    await onUpdateMachineCount(task.ScheduleTaskKey, parsed);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(task.ScheduleTaskKey);
    } catch {
      setDeleting(false);
    }
  };

  const bookings: BookingView[] = task.Bookings;
  const shares = evenSplit(task.TestQty, bookings.length);
  // Per-machine quantity (080): a machine's hand-tuned SplitQty overrides its even
  // share. Surface a drift hint when the tuned quantities don't sum back to the
  // task's Test Qty, so the planner can see they've over/under-allocated.
  const hasTunedQty = bookings.some((b) => b.SplitQty != null);
  const sumQty = bookings.reduce((acc, b, i) => acc + (b.SplitQty ?? shares[i] ?? 0), 0);
  const qtyDrift = hasTunedQty && task.TestQty != null && sumQty !== task.TestQty;
  // Run Mode Phase C: continuous tasks count CALENDAR days; machine time is
  // labelled elapsed 24/7 run-time. Attended groups unchanged.
  const duration = computeTaskDurationDays(task.StartDate, task.EndDate, holidays, task.RunMode);
  const durationDisplay = taskDurationDisplay(duration, task.RunMode);
  const mt = formatMachineTime(task.MachineMinutes, task.TestQty, task.IsPrepStep, task.RunMode);

  return (
    <>
      {/* ── Group header row ─────────────────────────────────────────────── */}
      <TableRow
        hover
        selected={selected}
        onClick={onActivateAll}
        sx={{
          cursor: 'pointer',
          opacity: deleting ? 0.4 : 1,
          transition: 'opacity 0.2s ease',
          backgroundColor: alpha(theme.schedule.machineTime, 0.06),
          '& td': { py: 1, px: 1.5, borderBottom: 'none' },
        }}
      >
        {bulkMode && (
          <TableCell padding="checkbox">
            <Checkbox
              size="small"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleSelect(task.ScheduleTaskKey)}
            />
          </TableCell>
        )}

        <TableCell align="center">
          <Typography variant="caption" color="text.secondary">{rowNumber}</Typography>
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
              {task.Name}
            </Typography>
            <Tooltip title={`Split across ${bookings.length} machines running in parallel. Each row below can be scheduled independently.`}>
              <Chip
                icon={<CallSplitIcon sx={{ fontSize: '0.7rem !important' }} />}
                label={`${bookings.length} machines`}
                size="small"
                sx={{
                  fontSize: '0.65rem',
                  height: 18,
                  backgroundColor: alpha(theme.schedule.machineTime, 0.12),
                  color: theme.schedule.machineTime,
                  '& .MuiChip-icon': { color: theme.schedule.machineTime },
                  cursor: 'default',
                }}
              />
            </Tooltip>
          </Box>
          {task.TaskTypeName && task.TaskTypeName !== task.Name && (
            <Typography variant="caption" color="text.secondary">{task.TaskTypeName}</Typography>
          )}
        </TableCell>

        <TableCell align="center">
          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
            {task.TestQty != null ? task.TestQty : '—'}
          </Typography>
          {qtyDrift && (
            <Tooltip title={`The per-machine quantities add up to ${sumQty}, not ${task.TestQty}. Adjust a machine's quantity to rebalance.`}>
              <Typography
                variant="caption"
                sx={{ display: 'block', fontSize: '0.62rem', fontWeight: 600, color: theme.schedule.conflictSoft }}
              >
                Σ {sumQty} of {task.TestQty}
              </Typography>
            </Tooltip>
          )}
        </TableCell>

        {/* Machine Qty editor — drives add/remove of the sub-rows (explicit apply) */}
        <TableCell align="center">
          <MachineQtyEditor liveCount={bookings.length} onApply={applyCount} />
        </TableCell>

        {/* Assigned machines summary — the actual selectors live on the sub-rows */}
        <TableCell>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.72rem' }}>
            Split across {bookings.length} machines (see rows below)
          </Typography>
        </TableCell>

        {/* Aggregate Start…End span (read-only — edit each machine on its row) */}
        <TableCell align="center">
          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            {fmtDate(task.StartDate)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            {fmtDate(task.EndDate)}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Tooltip title={`Total span across all machines — ${durationDisplay.tooltip} Each machine's own span is on its row.`}>
            <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
              {durationDisplay.text}
            </Typography>
          </Tooltip>
        </TableCell>

        <TableCell align="center">
          {mt ? (
            <Tooltip title={mt.tooltip}>
              <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600, color: theme.schedule.machineTime }}>
                {mt.text}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>—</Typography>
          )}
        </TableCell>

        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Tooltip title="Machine(s) placed">
              <CheckCircleOutline sx={{ fontSize: 16, color: theme.schedule.committed }} />
            </Tooltip>
            <Tooltip title="Remove task">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); void handleDelete(); }}
                  disabled={deleting}
                  sx={{
                    color: theme.schedule.conflictHard,
                    '&:hover': { backgroundColor: alpha(theme.schedule.conflictHard, 0.08) },
                  }}
                >
                  {deleting ? <CircularProgress size={14} /> : <Delete fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {/* ── One sub-row per machine ──────────────────────────────────────── */}
      {bookings.map((booking, i) => (
        <Phase1MachineSubRow
          key={booking.MachineBookingKey}
          task={task}
          booking={booking}
          index={i}
          total={bookings.length}
          share={shares[i] ?? null}
          bulkMode={bulkMode}
          selected={selected}
          holidays={holidays}
          onChangeMachine={onChangeMachine}
          onChangeBookingStart={onChangeBookingStart}
          onChangeBookingQty={onChangeBookingQty}
          onActivateBooking={onActivateBooking}
        />
      ))}

      {/* Machine-Qty-decrease confirmation. The editor re-syncs from the live booking
          count on its own once a confirmed reduce refetches the task. */}
      <ConfirmDialog
        state={confirmState}
        onClose={() => setConfirmState(CONFIRM_INITIAL)}
      />
    </>
  );
};
